import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { PropertyPermission } from 'src/modules/shared/types/permissions.type';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import React from 'react';
import type { CatalogProperty } from 'src/api/generated';
import { PropertyCardImage } from 'src/modules/shared/components/molecules/property-card-image';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Link } from 'src/modules/shared/components/atoms/links/link';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TagDropdown } from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import {
  PiDotsThreeVertical,
  PiMapPin,
  PiMapPinArea,
  PiPencilSimple,
} from 'react-icons/pi';
import type { PropertyCardViewModel } from '../../types/properties-list.types';
import { PropertyPricingCard } from './property-pricing-card';

type TranslateFn = (
  key: TranslationKey,
  params?: Record<string, string>
) => string;

interface PropertyListCardProps {
  card: PropertyCardViewModel;
  loadingFiles: boolean;
  renderAssignee: (property: CatalogProperty) => React.ReactNode;
  onEdit?: (property: CatalogProperty) => void;
  onDeactivate?: (property: CatalogProperty) => void;
  t: TranslateFn;
}

const getPropertyOpenLabel = (canUpdate: boolean, t: TranslateFn): string =>
  t(
    canUpdate
      ? 'catalog.properties.table.actions.edit'
      : 'catalog.properties.table.actions.view'
  );

const isPropertyCardImageLoading = (
  card: PropertyCardViewModel,
  loadingFiles: boolean
): boolean =>
  card.primaryImageFileId != null && card.imageUrl == null && loadingFiles;

export const PropertyListCard: React.FC<PropertyListCardProps> = ({
  card,
  loadingFiles,
  renderAssignee,
  onEdit,
  onDeactivate,
  t,
}) => {
  const {
    property,
    imageUrl,
    salePrice,
    rentPrice,
    commonExpenses,
    locationSummary,
    address,
    region,
    mapsUrl,
    mapsLabel,
    metrics,
    statusPills,
    warnings,
    updatedText,
    userActions,
  } = card;
  const { hasPermission: canUpdateAll } = useHasPermissions(
    PropertyPermission.Update
  );
  const { hasPermission: canUpdateScoped } = useHasPermissions(
    PropertyPermission.UpdateScoped
  );
  const { user } = useCurrentUser();
  const canUpdate =
    canUpdateAll ||
    (canUpdateScoped && user != null && property.userId === user.id);
  const handleEdit = () => onEdit?.(property);
  const propertyOpenLabel = getPropertyOpenLabel(canUpdate, t);
  const isImageLoading = isPropertyCardImageLoading(card, loadingFiles);

  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col lg:flex-row">
        <div className="relative aspect-[16/11] bg-slate-100 lg:h-auto lg:w-[200px] lg:flex-none xl:w-[220px]">
          {onEdit ? (
            <button
              type="button"
              onClick={handleEdit}
              className="block h-full w-full text-left"
              aria-label={propertyOpenLabel}
            >
              <PropertyCardImage
                imageUrl={imageUrl}
                alt={property.name}
                loading={isImageLoading}
                emptyLabel={t(
                  'catalog.properties.list.placeholder.noImageTitle'
                )}
              />
            </button>
          ) : (
            <PropertyCardImage
              imageUrl={imageUrl}
              alt={property.name}
              loading={isImageLoading}
              emptyLabel={t('catalog.properties.list.placeholder.noImageTitle')}
            />
          )}

          {warnings.length > 0 ? (
            <div className="absolute left-3 top-3 flex max-w-[calc(100%-1.5rem)] flex-wrap gap-2">
              {warnings.slice(0, 2).map((warning) => (
                <StatusPill
                  key={warning}
                  label={warning}
                  variant="warning"
                  appearance="filled"
                  className="bg-white/95"
                />
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4 p-4 sm:p-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px] lg:items-start">
            <div className="min-w-0 space-y-3">
              <div className="flex flex-wrap gap-2">{statusPills}</div>

              <div className="space-y-1.5">
                <Text size="tiny" className="text-slate-500">
                  <PropertyCode
                    displayCode={property.displayCode}
                    fallback={`#${property.id}`}
                  />
                  {property.cadastralId
                    ? ` · ${t('catalog.properties.table.cadastralId')} ${property.cadastralId}`
                    : ''}
                </Text>
                {onEdit ? (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="block max-w-full text-left"
                  >
                    <Text
                      as="h3"
                      size="h5"
                      weight="semibold"
                      className="truncate text-slate-950"
                    >
                      {property.name}
                    </Text>
                  </button>
                ) : (
                  <Text
                    as="h3"
                    size="h5"
                    weight="semibold"
                    className="truncate text-slate-950"
                  >
                    {property.name}
                  </Text>
                )}
                <div className="flex items-start gap-2">
                  <PiMapPinArea className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
                  <Text
                    size="small"
                    className={`truncate ${
                      locationSummary.length > 0 || region
                        ? 'text-slate-600'
                        : 'text-slate-400'
                    }`}
                    title={
                      locationSummary.length > 0
                        ? locationSummary
                        : region ?? undefined
                    }
                  >
                    {locationSummary.length > 0
                      ? locationSummary
                      : region ?? '-'}
                  </Text>
                </div>
                <div className="flex min-h-7 flex-wrap items-center gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <PiMapPin className="h-4 w-4 flex-none text-slate-400" />
                    {mapsUrl && mapsLabel ? (
                      <Link
                        href={mapsUrl}
                        size="tiny"
                        variant="primary"
                        color="primary"
                        underline="always"
                        showExternalIcon
                        className="min-w-0 truncate"
                        truncate
                      >
                        {mapsLabel}
                      </Link>
                    ) : (
                      <Text
                        size="tiny"
                        className={`truncate ${
                          address ? 'text-slate-500' : 'text-slate-400'
                        }`}
                        title={address ?? undefined}
                      >
                        {address ?? '-'}
                      </Text>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {metrics.map((metric) => (
                  <div
                    key={metric.key}
                    className={`rounded-full border px-3 py-1.5 ${
                      metric.isMissing
                        ? 'border-dashed border-slate-200 bg-slate-50'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <Text size="tiny" className="text-slate-500">
                      {metric.label}
                    </Text>{' '}
                    <Text
                      as="span"
                      size="tiny"
                      weight="semibold"
                      className={
                        metric.isMissing ? 'text-slate-400' : 'text-slate-900'
                      }
                    >
                      {metric.value}
                    </Text>
                  </div>
                ))}
              </div>
            </div>

            <PropertyPricingCard
              property={property}
              salePrice={salePrice}
              rentPrice={rentPrice}
              commonExpenses={commonExpenses}
              t={t}
            />
          </div>

          <div className="grid gap-3 border-t border-slate-100 pt-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="min-w-0 space-y-1">
              <Text size="tiny" weight="medium" className="text-slate-500">
                {t('catalog.properties.table.assigned')}
              </Text>
              {renderAssignee(property)}
            </div>

            <div className="flex flex-col gap-2 lg:items-end">
              <Text size="tiny" className="text-slate-500">
                {t('catalog.properties.list.updated', {
                  value: updatedText,
                })}
              </Text>

              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <Button
                  type="button"
                  variant="primary"
                  appearance="soft"
                  size="2xs"
                  className="gap-2"
                  onClick={() => onEdit?.(property)}
                >
                  <PiPencilSimple className="h-4 w-4" />
                  {t(
                    canUpdate
                      ? 'catalog.properties.table.actions.edit'
                      : 'catalog.properties.table.actions.view'
                  )}
                </Button>

                {canUpdate && userActions.length > 0 ? (
                  <TagDropdown
                    content={
                      <div className="flex items-center">
                        <PiDotsThreeVertical className="h-4 w-4" />
                      </div>
                    }
                    variant="primary"
                    appearance="soft"
                    size="2xs"
                    placement="right-start"
                    options={userActions}
                    onOptionSelect={(option) => {
                      if (option.id === 'deactivate') {
                        onDeactivate?.(property);
                      }
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};
